import { Link } from "react-router-dom";
import { ISSUE_CONTENT_LIVE } from "./InnerHustle";

/* ---------- ISSUE INDEX ----------
 * Add new issues here. Each upcoming issue gets `status: "upcoming"`,
 * each shipped issue gets `status: "published"`.
 */
const ISSUES = [
  {
    number: "001",
    status: ISSUE_CONTENT_LIVE ? "published" : "upcoming",
    title: "Becoming Her",
    dek: "Where awareness begins. A first chapter on intuition, burnout, and the work beneath the ambition.",
    season: "Winter 2026",
    cover: "/hero-edited.png",
    path: "/",
    stories: [
      "Becoming Her — Cover Essay",
      "The Real Hustle — Burnout & Intuition",
      "Passport to Peace — Travel",
      "Sixth Sense — Conversation",
      "In Rotation — Sade & bell hooks",
    ],
  },
];

const PublishedCard = ({ issue }) => (
  <Link
    to={issue.path}
    className="group block"
    data-testid={`archive-card-${issue.number}`}
  >
    <div className="relative overflow-hidden bg-black">
      <img
        src={issue.cover}
        alt={`Issue ${issue.number} — ${issue.title}`}
        className="w-full aspect-[16/9] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        style={{ filter: "saturate(0.92) contrast(1.04)" }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-1/2"
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0) 100%)",
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-x-0 bottom-0 px-6 md:px-10 pb-8 md:pb-10 text-[var(--ih-cream)]">
        <div
          className="font-mono-ui text-[10px] md:text-[11px] uppercase tracking-[0.42em] mb-3"
          style={{ color: "var(--ih-accent)" }}
        >
          Issue {issue.number} &middot; {issue.season}
        </div>
        <h2
          className="font-marquee leading-[0.95] tracking-[-0.01em]"
          style={{ fontSize: "clamp(2.4rem, 5.4vw, 4.4rem)" }}
        >
          {issue.title}
        </h2>
      </div>
    </div>

    <div className="mt-6 md:mt-8 grid grid-cols-12 gap-6">
      <div className="col-span-12 md:col-span-7">
        <p
          className="font-display italic leading-[1.45] text-[var(--ih-ink)]"
          style={{ fontSize: "clamp(1.15rem, 1.6vw, 1.35rem)", fontWeight: 500 }}
        >
          {issue.dek}
        </p>
      </div>
      <div className="col-span-12 md:col-span-5 md:pl-6 md:border-l border-[var(--ih-ink)]/15">
        <div className="font-mono-ui text-[10px] uppercase tracking-[0.42em] text-[var(--ih-muted)] mb-3">
          Inside this issue
        </div>
        <ul className="space-y-2 text-[0.95rem] text-[var(--ih-ink)] font-display">
          {issue.stories.map((s, i) => (
            <li key={i} className="flex items-start gap-3">
              <span
                className="font-mono-ui text-[9px] uppercase tracking-[0.32em] pt-1 shrink-0 opacity-50"
                style={{ minWidth: "20px" }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>

    <div className="mt-6 md:mt-8 inline-flex items-center gap-2 font-mono-ui text-[10px] uppercase tracking-[0.42em] text-[var(--ih-ink)] border-b border-[var(--ih-accent)] pb-1 group-hover:gap-3 transition-all">
      Read the issue
      <span aria-hidden="true">→</span>
    </div>
  </Link>
);

const UpcomingCard = ({ issue }) => {
  const kicker = issue ? `Issue ${issue.number} · Coming Soon` : "Issue 002 · Coming Soon";
  const title = issue ? issue.title : "Still in the writing.";
  const body = issue
    ? "This issue is being shaped now. Join the Circle to be the first to read it when it lands."
    : "The next chapter is being shaped now. Join the Circle to be notified when it lands.";
  return (
    <div
      className="border border-[var(--ih-ink)]/15 bg-[var(--ih-paper)] px-6 md:px-10 py-12 md:py-16 text-center"
      data-testid={`archive-card-${issue ? issue.number : "next"}`}
    >
      <div
        className="font-mono-ui text-[10px] uppercase tracking-[0.42em] mb-4"
        style={{ color: "var(--ih-accent)" }}
      >
        {kicker}
      </div>
      <h3
        className="font-marquee italic leading-[1.0] text-[var(--ih-ink)]"
        style={{ fontSize: "clamp(2rem, 3.6vw, 2.8rem)" }}
      >
        {title}
      </h3>
      <p
        className="mt-5 font-display italic text-[var(--ih-muted)] max-w-[460px] mx-auto"
        style={{ fontSize: "clamp(1rem, 1.4vw, 1.15rem)" }}
      >
        {body}
      </p>
      <Link
        to="/subscribe"
        className="mt-7 inline-flex items-center gap-2 font-mono-ui text-[10px] uppercase tracking-[0.42em] text-[var(--ih-ink)] border-b border-[var(--ih-accent)] pb-1"
        data-testid="archive-upcoming-subscribe"
      >
        Stay close
        <span aria-hidden="true">→</span>
      </Link>
    </div>
  );
};

const ArchivePage = () => (
  <div
    className="bg-[var(--ih-paper)] min-h-screen ih-grain relative"
    data-testid="archive-root"
  >
    <header className="max-w-[1100px] mx-auto px-6 md:px-10 pt-10 pb-2 flex items-center justify-between">
      <Link
        to="/"
        className="font-mono-ui text-[10px] uppercase tracking-[0.32em] ih-link"
        data-testid="archive-back"
      >
        ← Back to Home
      </Link>
      <Link
        to="/"
        className="font-mono-ui text-[10px] uppercase tracking-[0.32em]"
      >
        The Inner Hustle
      </Link>
    </header>

    <section className="max-w-[1100px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10">
      <div
        className="w-12 md:w-16 h-[2px] mb-8"
        style={{ background: "var(--ih-accent)" }}
        aria-hidden="true"
      />
      <div
        className="font-mono-ui text-[11px] uppercase tracking-[0.42em]"
        style={{ color: "var(--ih-accent)" }}
      >
        The Archive
      </div>
      <h1
        className="mt-6 font-marquee leading-[0.95] tracking-[-0.01em]"
        style={{ fontSize: "clamp(2.8rem, 7vw, 6.4rem)" }}
        data-testid="archive-title"
      >
        Every issue,
        <br />
        in one place.
      </h1>
      <p
        className="mt-6 font-display italic text-[var(--ih-ink)] max-w-[640px] leading-[1.4]"
        style={{ fontSize: "clamp(1.15rem, 1.6vw, 1.4rem)", fontWeight: 500 }}
      >
        A slow library of what we&rsquo;ve made. Read any chapter, any time.
      </p>
    </section>

    <main className="max-w-[1100px] mx-auto px-6 md:px-10 pb-24">
      <div className="space-y-20 md:space-y-28">
        {ISSUES.map((issue) =>
          issue.status === "published" ? (
            <PublishedCard key={issue.number} issue={issue} />
          ) : (
            <UpcomingCard key={issue.number} issue={issue} />
          )
        )}
        {/* only show the "next issue teaser" tile if the last issue is already published */}
        {ISSUES[ISSUES.length - 1]?.status === "published" && <UpcomingCard />}
      </div>
    </main>

    <footer className="max-w-[1100px] mx-auto px-6 md:px-10 pb-12 pt-6 border-t border-[var(--ih-ink)]/12 flex flex-col md:flex-row md:items-center md:justify-between gap-3 font-mono-ui text-[10px] uppercase tracking-[0.42em] text-[var(--ih-muted)]">
      <div>© 2026 The Inner Hustle</div>
      <div>Move differently.</div>
    </footer>
  </div>
);

export default ArchivePage;
